import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faHistory,
  faSearch,
  faSpinner,
  faTimes,
  faUndo,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import { buildAuditDiff } from '../../utils/auditDiff';
import { buildSalesEditDiff } from '../../utils/salesEditDiff';
import SearchableSelect from '../SearchableSelect';
import '../../styles/AuditTrailReport.css';

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;
const API_PATH = '/hospital-portal/api/audit-trail';

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 6);
  return { from: toISODate(from), to: toISODate(to) };
}

function emptyFilters() {
  const range = defaultDateRange();
  return {
    from: range.from,
    to: range.to,
    userId: '',
    changedBy: '',
    objectType: '',
    actionType: '',
    recordQuery: '',
    source: '',
  };
}

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function actionClass(actionType) {
  const key = String(actionType || '').toUpperCase();
  if (key === 'DELETE') return 'deleted';
  if (key === 'INSERT' || key === 'CREATE') return 'created';
  return 'updated';
}

function groupKey(row) {
  if (!row) return '';
  return `${row.object_type || ''}::${row.record_ref || row.record_id || ''}`;
}

function AuditDiffBlock({ event }) {
  if (!event) return null;
  const diff =
    event.source === 'sales_edit'
      ? { kind: 'sales', ...buildSalesEditDiff(event.old_data || {}, event.new_data || {}) }
      : { kind: 'generic', ...buildAuditDiff(event.old_data, event.new_data) };

  if (diff.kind === 'sales') {
    if (!diff.hasChanges) {
      return <p className="audit-drawer-status">No field differences recorded.</p>;
    }
    return (
      <div className="audit-diff">
        {diff.headerChanges.length > 0 && (
          <div className="audit-diff-section">
            <div className="audit-diff-title">Header</div>
            <ul>
              {diff.headerChanges.map((change) => (
                <li key={change.label}>
                  <span className="audit-diff-field">{change.label}:</span>{' '}
                  <span className="audit-diff-from">{change.from}</span>
                  <span className="audit-diff-arrow"> → </span>
                  <span className="audit-diff-to">{change.to}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {diff.lineChanges.length > 0 && (
          <div className="audit-diff-section">
            <div className="audit-diff-title">Line items</div>
            <ul>
              {diff.lineChanges.map((change, index) => (
                <li key={`${change.type}-${change.label}-${index}`}>
                  <span className={`audit-line-badge ${change.type}`}>
                    {change.type === 'added'
                      ? 'Added'
                      : change.type === 'removed'
                        ? 'Removed'
                        : 'Changed'}
                  </span>{' '}
                  {change.label}
                  {change.details && (
                    <div className="audit-line-details">{change.details.join(' · ')}</div>
                  )}
                  {change.fieldChanges && (
                    <ul className="audit-subchanges">
                      {change.fieldChanges.map((fieldChange) => (
                        <li key={fieldChange.label}>
                          <span className="audit-diff-field">{fieldChange.label}:</span>{' '}
                          <span className="audit-diff-from">{fieldChange.from}</span>
                          <span className="audit-diff-arrow"> → </span>
                          <span className="audit-diff-to">{fieldChange.to}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (!diff.hasChanges) {
    return <p className="audit-drawer-status">No field differences recorded.</p>;
  }

  return (
    <div className="audit-diff">
      <div className="audit-diff-section">
        <div className="audit-diff-title">Fields</div>
        <ul>
          {diff.changes.map((change) => (
            <li key={change.path}>
              <span className="audit-diff-field">{change.label}:</span>{' '}
              <span className="audit-diff-from">{change.from}</span>
              <span className="audit-diff-arrow"> → </span>
              <span className="audit-diff-to">{change.to}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const AuditTrailReport = () => {
  const { hasPermission } = useAuth();
  const canExport = hasPermission('/audit-trail', 'Export');

  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [meta, setMeta] = useState({ users: [], objectTypes: [], actionTypes: [] });
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({ total: 0, uniqueUsers: 0, uniqueObjects: 0, deletes: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const queryParams = useMemo(() => {
    const params = {
      from: applied.from,
      to: applied.to,
      page,
      limit,
    };
    if (applied.userId) params.userId = applied.userId;
    if (applied.changedBy) params.changedBy = applied.changedBy;
    if (applied.objectType) params.objectType = applied.objectType;
    if (applied.actionType) params.actionType = applied.actionType;
    if (applied.recordQuery) params.recordQuery = applied.recordQuery;
    if (applied.source) params.source = applied.source;
    return params;
  }, [applied, page, limit]);

  const fetchMeta = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}${API_PATH}/meta`);
      setMeta({
        users: response.data.users || [],
        objectTypes: response.data.objectTypes || [],
        actionTypes: response.data.actionTypes || [],
      });
    } catch (err) {
      console.error('Failed to load audit filter options', err);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${BASE_URL}${API_PATH}`, { params: queryParams });
      setGroups(response.data.groups || []);
      setStats(response.data.stats || { total: 0, uniqueUsers: 0, uniqueObjects: 0, deletes: 0 });
      setPagination(response.data.pagination || { page: 1, limit, total: 0, pages: 0 });
    } catch (err) {
      setGroups([]);
      setError(err.response?.data?.error || 'Failed to load the audit trail.');
    } finally {
      setLoading(false);
    }
  }, [queryParams, limit]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = (nextFilters = draft) => {
    setApplied(nextFilters);
    setDraft(nextFilters);
    setPage(1);
    setSelected(null);
    setDetail(null);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    applyFilters(draft);
  };

  const handleClear = () => {
    applyFilters(emptyFilters());
  };

  const openGroup = async (groupRow) => {
    setSelected(groupRow);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${API_PATH}/group`, {
        params: {
          objectType: groupRow.object_type,
          recordRef: groupRow.record_ref || groupRow.record_id,
          from: applied.from,
          to: applied.to,
          ...(applied.source ? { source: applied.source } : {}),
        },
      });
      setDetail(response.data);
    } catch (err) {
      setDetailError(err.response?.data?.error || 'Could not load this record history.');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
    setDetailError('');
  };

  const filterToObject = (row) => {
    applyFilters({
      ...applied,
      objectType: row.object_type || '',
      recordQuery: row.record_ref || row.record_id || '',
      source: '',
    });
  };

  const filterToUser = (eventRow) => {
    const matched = meta.users.find(
      (user) =>
        user.name === eventRow.changed_by ||
        user.name === eventRow.last_changed_by ||
        String(user.id) === String(eventRow.changed_by_raw || eventRow.last_changed_by_raw)
    );
    applyFilters({
      ...applied,
      userId: matched ? String(matched.id) : '',
      changedBy: matched
        ? ''
        : (eventRow.changed_by_raw ||
          eventRow.last_changed_by_raw ||
          eventRow.changed_by ||
          eventRow.last_changed_by ||
          ''),
    });
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError('');
      const params = { ...queryParams };
      delete params.page;
      delete params.limit;
      const response = await axios.get(`${BASE_URL}${API_PATH}/export`, {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-trail-${applied.from}-to-${applied.to}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = 'Failed to export the audit trail.';
      const data = err.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.error || message;
        } catch {
          /* keep default */
        }
      } else if (data?.error) {
        message = data.error;
      }
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  const pages = pagination.pages || 0;
  const groupTotal = pagination.total || 0;
  const showingFrom = groupTotal === 0 ? 0 : (page - 1) * limit + 1;
  const showingTo = Math.min(page * limit, groupTotal);

  const personOptions = useMemo(
    () => [
      { value: '', label: 'Everyone' },
      ...meta.users.map((user) => ({ value: String(user.id), label: user.name })),
    ],
    [meta.users]
  );

  const objectOptions = useMemo(
    () => [
      { value: '', label: 'All objects' },
      ...meta.objectTypes.map((type) => ({ value: type.value, label: type.label })),
    ],
    [meta.objectTypes]
  );

  return (
    <div className="audit-trail">
      <div className="audit-trail-header">
        <div>
          <h1>
            <FontAwesomeIcon icon={faHistory} /> Audit Trail
          </h1>
          <p>See who changed what, and when. Start with a date range, then narrow by person or record.</p>
        </div>
        {canExport && (
          <button
            type="button"
            className="audit-btn audit-btn-export"
            onClick={handleExport}
            disabled={exporting || loading}
          >
            <FontAwesomeIcon icon={exporting ? faSpinner : faDownload} spin={exporting} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        )}
      </div>

      <div className="audit-stats">
        <div className="audit-stat-card">
          <div className="audit-stat-value">{stats.total}</div>
          <div className="audit-stat-label">Events</div>
        </div>
        <div className="audit-stat-card">
          <div className="audit-stat-value">{stats.uniqueUsers}</div>
          <div className="audit-stat-label">People</div>
        </div>
        <div className="audit-stat-card">
          <div className="audit-stat-value">{stats.uniqueObjects}</div>
          <div className="audit-stat-label">Records</div>
        </div>
        <div className="audit-stat-card deletes">
          <div className="audit-stat-value">{stats.deletes}</div>
          <div className="audit-stat-label">Deletes</div>
        </div>
      </div>

      <form className="audit-filters" onSubmit={handleSearch}>
        <div className="audit-filter-group">
          <label htmlFor="audit-from">From</label>
          <input
            id="audit-from"
            type="date"
            value={draft.from}
            onChange={(e) => updateDraft('from', e.target.value)}
            required
          />
        </div>
        <div className="audit-filter-group">
          <label htmlFor="audit-to">To</label>
          <input
            id="audit-to"
            type="date"
            value={draft.to}
            onChange={(e) => updateDraft('to', e.target.value)}
            required
          />
        </div>
        <div className="audit-filter-group">
          <label>Person</label>
          <SearchableSelect
            value={draft.userId}
            options={personOptions}
            placeholder="Everyone"
            emptyMessage="No matching people"
            onChange={(opt) =>
              setDraft((prev) => ({ ...prev, userId: opt.value || '', changedBy: '' }))
            }
          />
        </div>
        <div className="audit-filter-group">
          <label>Object</label>
          <SearchableSelect
            value={draft.objectType}
            options={objectOptions}
            placeholder="All objects"
            emptyMessage="No matching objects"
            onChange={(opt) => updateDraft('objectType', opt.value || '')}
          />
        </div>
        <div className="audit-filter-group">
          <label htmlFor="audit-action">Action</label>
          <select
            id="audit-action"
            value={draft.actionType}
            onChange={(e) => updateDraft('actionType', e.target.value)}
          >
            <option value="">All actions</option>
            {meta.actionTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div className="audit-filter-group">
          <label htmlFor="audit-record">Record ID / sales number</label>
          <input
            id="audit-record"
            type="text"
            value={draft.recordQuery}
            onChange={(e) => updateDraft('recordQuery', e.target.value)}
            placeholder="e.g. 1042 or INV-123"
          />
        </div>
        <div className="audit-filter-group">
          <label htmlFor="audit-source">Source</label>
          <select
            id="audit-source"
            value={draft.source}
            onChange={(e) => updateDraft('source', e.target.value)}
          >
            <option value="">All sources</option>
            <option value="audit_trail">Data changes</option>
            <option value="sales_edit">Sales edits</option>
          </select>
        </div>
        <div className="audit-filter-actions">
          <button type="submit" className="audit-btn audit-btn-primary" disabled={loading}>
            <FontAwesomeIcon icon={faSearch} />
            Search
          </button>
          <button type="button" className="audit-btn audit-btn-secondary" onClick={handleClear}>
            <FontAwesomeIcon icon={faUndo} />
            Reset
          </button>
        </div>
      </form>

      {error && <div className="audit-error">{error}</div>}

      <div className={`audit-layout ${selected ? 'has-drawer' : ''}`}>
        <div className="audit-main">
          <div className="audit-table-wrap">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Last change</th>
                  <th>Record</th>
                  <th>Object</th>
                  <th>Changes</th>
                  <th>Last person</th>
                  <th>Latest action</th>
                </tr>
              </thead>
              <tbody>
                {loading && groups.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="audit-empty">
                      <FontAwesomeIcon icon={faSpinner} spin /> Loading audit events…
                    </td>
                  </tr>
                ) : groups.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="audit-empty">
                      No audit events in this date range. Try widening the dates.
                    </td>
                  </tr>
                ) : (
                  groups.map((groupRow) => (
                    <tr
                      key={groupKey(groupRow)}
                      className={groupKey(selected) === groupKey(groupRow) ? 'is-selected' : ''}
                      onClick={() => openGroup(groupRow)}
                    >
                      <td>{formatWhen(groupRow.last_changed_at)}</td>
                      <td>{groupRow.record_ref || groupRow.record_id || '—'}</td>
                      <td>{groupRow.object_label}</td>
                      <td>{groupRow.event_count}</td>
                      <td>{groupRow.last_changed_by || '—'}</td>
                      <td>
                        <span className={`audit-badge ${actionClass(groupRow.last_action_type)}`}>
                          {groupRow.last_action_label}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="audit-pagination">
            <div className="audit-page-info">
              {groupTotal > 0
                ? `Showing ${showingFrom}–${showingTo} of ${groupTotal}`
                : 'No results'}
            </div>
            <div className="audit-page-controls">
              <label>
                Per page
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(parseInt(e.target.value, 10));
                    setPage(1);
                  }}
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </label>
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>
                Page {pages ? page : 0} of {pages}
              </span>
              <button
                type="button"
                disabled={page >= pages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {selected && (
          <aside className="audit-drawer">
            <div className="audit-drawer-header">
              <h2>Record history</h2>
              <button type="button" className="audit-icon-btn" onClick={closeDetail} title="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {detailLoading ? (
              <p className="audit-drawer-status">
                <FontAwesomeIcon icon={faSpinner} spin /> Loading details…
              </p>
            ) : detailError ? (
              <p className="audit-error">{detailError}</p>
            ) : detail?.record ? (
              <>
                <dl className="audit-meta">
                  <div>
                    <dt>Object</dt>
                    <dd>
                      {detail.record.object_label}{' '}
                      {detail.record.record_ref ? `#${detail.record.record_ref}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt>Updates</dt>
                    <dd>{detail.record.event_count}</dd>
                  </div>
                </dl>

                <div className="audit-chips">
                  <button
                    type="button"
                    onClick={() => filterToUser(detail.events?.[0] || selected)}
                  >
                    Show this person only
                  </button>
                  <button type="button" onClick={() => filterToObject(detail.record)}>
                    Show this record only
                  </button>
                </div>

                <div className="audit-timeline">
                  {(detail.events || []).map((event) => (
                    <article
                      key={`${event.source}-${event.id}`}
                      className="audit-timeline-entry"
                    >
                      <div className="audit-timeline-meta">
                        <span className={`audit-badge ${actionClass(event.action_type)}`}>
                          {event.action_label}
                        </span>
                        <span>{formatWhen(event.changed_at)}</span>
                      </div>
                      <div className="audit-timeline-who">{event.changed_by || '—'}</div>
                      <AuditDiffBlock event={event} />
                      <details className="audit-raw">
                        <summary>Raw data</summary>
                        <pre>
                          {JSON.stringify({ old: event.old_data, new: event.new_data }, null, 2)}
                        </pre>
                      </details>
                    </article>
                  ))}
                </div>
              </>
            ) : null}
          </aside>
        )}
      </div>
    </div>
  );
};

export default AuditTrailReport;
